import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, Link, useNavigate } from "@tanstack/react-router";
import { Badge, Button, IconArrowLeft, IconCheck, Notice, TextField, useToast } from "@wilco/ui";
import { useEffect, useRef, useState, type SubmitEvent } from "react";
import { formatDate, formatNumber, t } from "../i18n.ts";
import { latinizeCode } from "../scan/layout.ts";
import { ScanBar } from "../scan/ScanBar.tsx";
import { stockApi } from "./api.ts";
import { parseQty } from "./qty.ts";
import { issueQuery, STOCK } from "./queries.ts";
import { errorNotice, type NoticeState } from "./ShelfPage.tsx";
import type { IssueLine } from "./types.ts";

const route = getRouteApi("/app/stock/issue/$id");

/**
 * «Видати на склад» по заявке склада деятельности: строки по маршруту
 * обхода полок. Скан полки открывает её строку, кладовщик вводит взятое.
 * Недобор не держит заявку: она закрывается, недостающее уходит следующей
 * заявкой (INV-23). Главная кнопка одна: «Видати» главная, только когда
 * все строки взяты и строка не открыта.
 */
export function IssuePage() {
  const { id } = route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const card = useQuery(issueQuery(id));
  const [taken, setTaken] = useState<Record<string, number>>({});
  const [current, setCurrent] = useState<string | null>(null);
  const [notice, setNotice] = useState<NoticeState | null>(null);
  const issue = useMutation({ mutationFn: () => stockApi.issue(id, { lines: Object.entries(taken).map(([lineId, qty]) => ({ id: lineId, qty })) }) });

  const c = card.data;
  const lines = c?.lines ?? [];
  const pickedCount = lines.filter((l) => taken[l.id] !== undefined).length;
  const complete = lines.length > 0 && pickedCount === lines.length;

  const scan = (raw: string) => {
    setNotice(null);
    const code = latinizeCode(raw);
    const line = lines.find((l) => l.shelf === code) ?? lines.find((l) => l.product.sku === code);
    if (line) setCurrent(line.id);
    else setNotice({ tone: "danger", title: t("issue.notInRequest", { code: raw }) });
  };

  const take = (line: IssueLine, qty: number) => {
    setTaken((prev) => ({ ...prev, [line.id]: qty }));
    // Следующая невзятая строка по маршруту.
    const next = lines.find((l) => l.id !== line.id && taken[l.id] === undefined);
    setCurrent(next?.id ?? null);
  };

  const validate = () => {
    issue.mutate(undefined, {
      onSuccess: (r) => {
        void queryClient.invalidateQueries({ queryKey: STOCK });
        const short = r.short.map((s) => `${s.sku} ${formatNumber(s.qty)}`).join(", ");
        toast({
          title: t("issue.done", { number: r.number }),
          description: short ? t("issue.short", { lines: short }) : undefined,
          tone: short ? "info" : "success",
        });
        void navigate({ to: "/stock/today", search: { tab: "issue" } });
      },
      onError: (e) => setNotice(errorNotice(e)),
    });
  };

  return (
    <div className="page stock-narrow">
      <div className="page-head">
        <Button asChild variant="ghost" size="icon">
          <Link to="/stock/today" search={{ tab: "issue" }} aria-label={t("issue.back")}>
            <IconArrowLeft />
          </Link>
        </Button>
        <h1 className="page-title w-num">{c?.number ?? ""}</h1>
      </div>
      {card.error ? <Notice tone="danger" title={errorNotice(card.error).title} className="stock-notice" /> : null}
      {c ? (
        <>
          <div className="issue-head">
            <Badge tone="strong">{c.warehouse.name}</Badge>
            {c.order ? <span className="w-num">{c.order}</span> : null}
            <span className={c.late ? "queue-late" : "w-muted"}>
              {t("issue.need", { date: formatDate(c.needDate) })}
              {c.late ? ` · ${t("today.late")}` : ""}
            </span>
          </div>
          <div
            className="progress"
            role="progressbar"
            aria-label={t("issue.progress")}
            aria-valuemin={0}
            aria-valuemax={lines.length}
            aria-valuenow={pickedCount}
          >
            {/* Масштаб, а не ширина: полоса растёт без перекладки («13»). */}
            <span className="progress-bar" style={{ transform: `scaleX(${String(lines.length ? pickedCount / lines.length : 0)})` }} />
          </div>
          <p className="w-muted">{t("issue.lead", { picked: pickedCount, total: lines.length })}</p>
          <ScanBar onScan={scan} placeholder={t("issue.scan")} autoFocus={current === null} />
          {notice ? <Notice tone={notice.tone} title={notice.title} className="stock-notice" /> : null}
          <ol className="issue-lines">
            {lines.map((l) => (
              <li key={l.id}>
                <Line line={l} taken={taken[l.id]} open={current === l.id} onOpen={() => setCurrent(l.id)} onTake={(q) => take(l, q)} />
              </li>
            ))}
          </ol>
          <Button
            variant={complete && current === null ? "primary" : "secondary"}
            size="lg"
            block
            disabled={pickedCount === 0 || issue.isPending}
            onClick={validate}
          >
            {t("issue.validate")}
          </Button>
        </>
      ) : null}
    </div>
  );
}

function Line({
  line,
  taken,
  open,
  onOpen,
  onTake,
}: {
  line: IssueLine;
  taken: number | undefined;
  open: boolean;
  onOpen: () => void;
  onTake: (qty: number) => void;
}) {
  const uom = line.product.uom;
  const short = taken !== undefined && taken < line.qty;
  return (
    <div className="issue-line" data-open={open ? "" : undefined} data-done={taken !== undefined ? "" : undefined}>
      <button type="button" className="issue-line-head" onClick={onOpen} aria-expanded={open}>
        <span className="w-num queue-shelf">{line.shelf ?? t("today.noShelf")}</span>
        <span className="queue-name">
          <span className="w-num">{line.product.sku}</span> {line.product.name}
        </span>
        <span className="w-num queue-qty">
          {taken !== undefined ? (
            <>
              {short ? null : <IconCheck className="issue-check" />}
              {`${formatNumber(taken)} / `}
            </>
          ) : null}
          {`${formatNumber(line.qty)}\u00a0${uom}`}
        </span>
        {line.onShelf < line.qty && taken === undefined ? (
          <span className="issue-warn">{t("issue.onShelf", { qty: formatNumber(line.onShelf), uom })}</span>
        ) : null}
        {short ? <span className="issue-warn">{t("issue.lineShort", { qty: formatNumber(line.qty - taken), uom })}</span> : null}
      </button>
      {open ? <TakeForm line={line} onTake={onTake} /> : null}
    </div>
  );
}

function TakeForm({ line, onTake }: { line: IssueLine; onTake: (qty: number) => void }) {
  // Подставлено сколько можно взять: заявка или остаток полки, что меньше.
  const [raw, setRaw] = useState(String(Math.min(line.qty, line.onShelf)));
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const qty = parseQty(raw);
    if (qty === null || qty < 0) {
      setError(t("shelf.qtyRequired"));
      return;
    }
    if (qty > line.qty) {
      setError(t("issue.overLine", { qty: formatNumber(line.qty) }));
      return;
    }
    onTake(qty);
  };

  return (
    <form className="qty-row issue-take" onSubmit={submit}>
      <TextField
        ref={ref}
        label={t("issue.taken")}
        inputMode="decimal"
        autoComplete="off"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        onFocus={(e) => e.target.select()}
        error={error ?? undefined}
        className="qty-field"
      />
      <span className="qty-uom">{line.product.uom}</span>
      <Button type="submit" variant="primary" className="qty-submit">
        {t("issue.take")}
      </Button>
    </form>
  );
}
